import AdditionProblemGenerator, { AdditionOptions } from "./addition";
import DivisionProblemGenerator from "./division";
import MultiplicationProblemGenerator from "./multiplication";
import { OperandOption, IProblem, IProblemGenerator } from "./sharedTypes";
import SubtractionProblemGenerator from "./subtraction";
export type CombinedProblemConfigs = {
    progressionRate?: number;
    addition?: {
        frequency: number;
        maxResult?: number;
        operand1?: OperandOption;
        operand2?: OperandOption;
        progression?: boolean;
        includeInProgression?: boolean;
    };
    subtraction?: {
        frequency: number;
        operand1?: OperandOption;
        operand2?: OperandOption;
        progression?: boolean;
        includeInProgression?: boolean;
    };
    multiplication?: {
        frequency: number;
        operand1?: OperandOption;
        operand2?: OperandOption;
        progression?: boolean;
        includeInProgression?: boolean;
    };
    division?: {
        frequency: number;
        operand1?: OperandOption;
        operand2?: OperandOption
        progression?: boolean;
        includeInProgression?: boolean;
    };
}
export const defaultConfigs : CombinedProblemConfigs = {
    progressionRate: 5,
    addition: {
        frequency: 5,
        maxResult: 21,
        operand1: {
            min: 0,
            max: 21
        },
        operand2: {
            min: 0,
            max: 21
        }
    },
    subtraction: {
        frequency: 20,
        operand1: {
            min: 10,
            max: 20
        },
        operand2: {
            min: 0,
            max: 20
        }
    },
    multiplication: {
        frequency: 2,
        operand1: {
            min: 0,
            max: 9
        },
        operand2: {
            min: 0,
            max: 3
        }
    },
    division: {
        frequency: 0,
        operand1: {
            min: 1,
            max: 9
        },
        operand2: {
            min: 1,
            max: 9
        }
    }
}

export default class CombinedProblemGenerator implements IProblemGenerator {
    private addition: AdditionProblemGenerator | undefined;
    private subtraction: SubtractionProblemGenerator | undefined;
    private multiplication: MultiplicationProblemGenerator | undefined;
    private division: DivisionProblemGenerator | undefined;
    private options: CombinedProblemConfigs;

    private lastProblem: IProblem | undefined;
    private lastGenerator: IProblemGenerator | undefined;
    private currentStreak: number;

    constructor(options: CombinedProblemConfigs) {
        this.options = options;
        this.currentStreak = 0;

        if (options.addition) {
            Object.assign(options.addition, { progressionRate: this.options.progressionRate })
            this.addition = new AdditionProblemGenerator(options.addition);
        }

        if (options.subtraction) {
            Object.assign(options.subtraction, { progressionRate: this.options.progressionRate })
            this.subtraction = new SubtractionProblemGenerator(options.subtraction);
        }

        if (options.multiplication) {
            Object.assign(options.multiplication, { progressionRate: this.options.progressionRate })
            this.multiplication = new MultiplicationProblemGenerator(options.multiplication);
        }

        if (options.division) {
            Object.assign(options.division, { progressionRate: this.options.progressionRate })
            this.division = new DivisionProblemGenerator(options.division);
        }

    }
    getNext() {
        const rnd = Math.random()*((this.options?.addition?.frequency ?? 0) + (this.options?.subtraction?.frequency ?? 0) + (this.options?.multiplication?.frequency ?? 0) + (this.options?.division?.frequency ?? 0));
        let problemOut : IProblem | undefined = undefined

        while (true)
        {
            if (rnd < (this.options?.addition?.frequency ?? 0)) {
                problemOut = this.addition?.getNext();
                this.lastGenerator = this.addition;
            }
            else if (rnd < ((this.options?.addition?.frequency ?? 0) + (this.options?.subtraction?.frequency ?? 0))) {
                problemOut = this.subtraction?.getNext();
                this.lastGenerator = this.subtraction;
            }
            else if (rnd < ((this.options?.addition?.frequency ?? 0) + (this.options?.subtraction?.frequency ?? 0) + (this.options?.multiplication?.frequency ?? 0))) {
                problemOut = this.multiplication?.getNext();
                this.lastGenerator = this.multiplication;
            }
            else {
                problemOut = this.division?.getNext();
                this.lastGenerator = this.division;
            }

            // prevent immediate duplicate problems
            if (!problemOut)
                throw new Error(`No problem generators found!`);

            if (problemOut.display !== this.lastProblem?.display) {
                this.lastProblem = problemOut;
                return problemOut;
            }
        }
    }
    reportResult(wasCorrect: boolean, userAnswer: number) {
        // this class determins when new generators are added
        this.currentStreak = wasCorrect ? this.currentStreak + 1 : 0;
        //console.log(`Top level current streak: `, this.currentStreak)

        // we let each generator internally address their own progression
        if (this.lastGenerator?.reportResult)
            this.lastGenerator.reportResult(wasCorrect, userAnswer);
    }
}

